import React, { createContext, useContext, useState, useEffect } from 'react';
import { contactInfo as fallbackContactInfo, services as fallbackServices, fallbackMediaMentions } from '../data';

const CmsContext = createContext(null);

export function CmsProvider({ children }) {
  const [siteSettings, setSiteSettings] = useState({
    company_name: fallbackContactInfo.company,
    phone: fallbackContactInfo.phone,
    email: fallbackContactInfo.email,
    address: fallbackContactInfo.address,
    cvr: '45058727',
    opening_hours: 'Man - Fre: 08:00 - 17:00\nLørdag: 09:00 - 13:00\nSøndag: Lukket',
    hours: fallbackContactInfo.hours,
    logo: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png',
    facebook_url: 'https://www.facebook.com/share/18U7n9sHek/?mibextid=wwXIfr',
    linkedin_url: 'https://www.linkedin.com/company/ecolaser-aps/',
    instagram_url: 'https://www.instagram.com/ecolaser_dk',
    phoneTel: `tel:${fallbackContactInfo.phone.replace(/\s+/g, '')}`,
    emailMailto: `mailto:${fallbackContactInfo.email}`
  });

  const [pages, setPages] = useState([]);
  const [services, setServices] = useState(fallbackServices);
  const [faq, setFaq] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [cases, setCases] = useState([]);
  const [mediaMentions, setMediaMentions] = useState(fallbackMediaMentions);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all collections in parallel from the public API
    Promise.allSettled([
      fetch('https://api.dev.auxo.dk/public/content/site_settings').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/pages').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/services').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/faq').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/testimonials').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/gallery').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/cases').then(r => r.json()),
      fetch('https://api.dev.auxo.dk/public/content/media_mentions').then(r => r.json())
    ]).then(([settingsRes, pagesRes, servicesRes, faqRes, testimonialsRes, galleryRes, casesRes, mediaRes]) => {
      // 1. Site Settings
      if (settingsRes.status === 'fulfilled' && Array.isArray(settingsRes.value) && settingsRes.value.length > 0) {
        const item = settingsRes.value[0];
        const rawPhone = item.phone || fallbackContactInfo.phone;
        const rawEmail = item.email || fallbackContactInfo.email;
        const rawAddress = typeof item.address === 'object' ? item.address?.da : item.address || fallbackContactInfo.address;
        const rawCompany = typeof item.company_name === 'object' ? item.company_name?.da : item.company_name || fallbackContactInfo.company;
        const rawHours = typeof item.opening_hours === 'object' ? item.opening_hours?.da : item.opening_hours || 'Man - Fre: 08:00 - 17:00\nLørdag: 09:00 - 13:00\nSøndag: Lukket';
        
        // Parse opening hours lines if possible
        const hoursLines = rawHours ? rawHours.split('\n').filter(Boolean).map(line => {
          const parts = line.split(':');
          if (parts.length >= 2) {
            return { days: parts[0].trim(), time: parts.slice(1).join(':').trim() };
          }
          return { days: line, time: '' };
        }) : fallbackContactInfo.hours;

        setSiteSettings({
          company_name: rawCompany,
          phone: rawPhone,
          email: rawEmail,
          address: rawAddress,
          cvr: item.cvr || '45058727',
          opening_hours: rawHours,
          hours: hoursLines.length > 0 ? hoursLines : fallbackContactInfo.hours,
          logo: Array.isArray(item.logo) && item.logo.length > 0 ? item.logo[0] : 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png',
          facebook_url: item.facebook_url || 'https://www.facebook.com/share/18U7n9sHek/?mibextid=wwXIfr',
          linkedin_url: item.linkedin_url || 'https://www.linkedin.com/company/ecolaser-aps/',
          instagram_url: item.instagram_url || 'https://www.instagram.com/ecolaser_dk',
          phoneTel: `tel:${rawPhone.replace(/\s+/g, '')}`,
          emailMailto: `mailto:${rawEmail}`
        });
      }

      // 2. Pages
      if (pagesRes.status === 'fulfilled' && Array.isArray(pagesRes.value)) {
        setPages(pagesRes.value);
      }

      // 3. Services
      if (servicesRes.status === 'fulfilled' && Array.isArray(servicesRes.value) && servicesRes.value.length > 0) {
        const sorted = [...servicesRes.value].sort((a, b) => ((a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99) - (b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99)));
        setServices(sorted);
      }

      // 4. FAQ
      if (faqRes.status === 'fulfilled' && Array.isArray(faqRes.value) && faqRes.value.length > 0) {
        const sorted = [...faqRes.value].sort((a, b) => ((a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99) - (b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99)));
        setFaq(sorted);
      }

      // 5. Testimonials
      if (testimonialsRes.status === 'fulfilled' && Array.isArray(testimonialsRes.value) && testimonialsRes.value.length > 0) {
        setTestimonials(testimonialsRes.value);
      }

      // 6. Gallery
      if (galleryRes.status === 'fulfilled' && Array.isArray(galleryRes.value) && galleryRes.value.length > 0) {
        const sorted = [...galleryRes.value].sort((a, b) => ((a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99) - (b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99)));
        setGallery(sorted);
      }

      // 7. Cases
      if (casesRes.status === 'fulfilled' && Array.isArray(casesRes.value) && casesRes.value.length > 0) {
        const sorted = [...casesRes.value].sort((a, b) => ((a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99) - (b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99)));
        setCases(sorted);
      }

      // 8. Media Mentions
      if (mediaRes.status === 'fulfilled' && Array.isArray(mediaRes.value) && mediaRes.value.length > 0) {
        const sorted = [...mediaRes.value].sort((a, b) => ((a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99) - (b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99)));
        setMediaMentions(sorted);
      }
    }).catch(err => {
      console.error('Fejl under indlæsning af CMS data:', err);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  // Helper function to get page data by slug or keyword
  const getPage = (slugOrKey) => {
    if (!pages || pages.length === 0) return null;
    return pages.find(p => {
      const pSlug = typeof p.slug === 'object' ? p.slug?.da : p.slug;
      if (pSlug === slugOrKey) return true;
      const title = typeof p.title === 'object' ? p.title?.da : p.title;
      return title && title.toLowerCase().includes(slugOrKey.toLowerCase());
    }) || null;
  };

  const value = {
    siteSettings,
    pages,
    getPage,
    services,
    faq,
    testimonials,
    gallery,
    cases,
    mediaMentions,
    loading
  };

  return (
    <CmsContext.Provider value={value}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms() {
  const context = useContext(CmsContext);
  if (!context) {
    throw new Error('useCms must be used within a CmsProvider');
  }
  return context;
}
